import * as XLSX from "xlsx";

export const FILE_ENCODINGS = {
  UTF_8: "UTF-8",
  /**
   * Expected encoding for TSV files:
   */
  TSV: "ISO-8859-1",
};

/**
 * @param {File}   file
 * @param {String} encoding
 *
 * @return {Promise<String>} Promise will read file content.
 */
export function readFileContent(file, encoding = FILE_ENCODINGS.UTF_8) {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    const type = file.name.split(".").pop();

    if (type === "ods") {
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
  
          // Convert the first sheet to TSV
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
  
          // Convert sheet to CSV with tab as separator
          const tsv = XLSX.utils.sheet_to_csv(sheet, { FS: "\t" });
          resolve(tsv);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file, encoding);
      reader.onloadend = () => {
        resolve(reader.result);
      };
    }

    reader.onerror = () => {
      reject();
    };
  });
}

/**
 * Downloads a file from an URL
 * @param {string} url - The URL of the file to download
 * @param {string} fileName - The name of the file to download
 */
export function downloadFile(url, fileName) {
  // Create a download link
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  // Clean up
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Processes specified columns in a TSV content using a given function.
 *
 * @param {string} tsvContent - The raw TSV content as a string.
 * @param {string[]} targetHeaders - The headers of columns that should be processed.
 * @param {Function} processFunction - A function to process the values in the specified columns.
 * @returns {string} - The updated TSV content.
 */
export function processTsvColumns(tsvContent, targetHeaders, processFunction) {
  const rows = tsvContent.split("\n").map(row => row.trimEnd());
  if (rows.length < 2) return tsvContent; // Ensure there are headers and at least one row of data

  const headers = rows[0].split("\t");
  const targetIndexes = targetHeaders.map(header => headers.indexOf(header)).filter(index => index !== -1);

  if (targetIndexes.length === 0) return tsvContent; // No valid headers found

  const updatedRows = rows.map((row, rowIndex) => {
    const columns = row.split("\t");

    if (rowIndex > 0) { // Skip headers row
      targetIndexes.forEach(index => {
        columns[index] = processFunction(columns[index]);
      });
    }

    return columns.join("\t");
  });

  return updatedRows.join("\n");
}